import { computed, ref, watch, type Ref } from 'vue';
import { fetchCustomerAccount } from '@/api/customer';
import { fetchCompany } from '@/api/company';
import { fetchProducts } from '@/api/product';
import type { IProduct } from '@/types/product';

export type EntityType = 'customer.account' | 'company' | 'product';

export interface ResolvedEntity {
  ref: string;
  type: EntityType;
  id: number;
  name: string;
  status: 'loading' | 'resolved' | 'error';
}

const VALID_TYPES: EntityType[] = ['customer.account', 'company', 'product'];

export function parseRef(refString: string): { type: EntityType; id: number } | null {
  const lastColon = refString.lastIndexOf(':');
  if (lastColon === -1) return null;

  const type = refString.substring(0, lastColon);
  const id = parseInt(refString.substring(lastColon + 1), 10);

  if (!VALID_TYPES.includes(type as EntityType) || isNaN(id)) return null;
  return { type: type as EntityType, id };
}

export function buildRef(type: EntityType, id: number): string {
  return `${type}:${id}`;
}

export function formatAccountName(account: { firstname?: string; lastname?: string; email?: string }): string {
  return [account.firstname, account.lastname].filter(Boolean).join(' ') || account.email || '';
}

let moduleProductCache: IProduct[] | null = null;

export function useEntityReferenceResolver(refs: Ref<string[]>) {
  const cache = new Map<string, ResolvedEntity>();
  const resolvedEntities = ref<ResolvedEntity[]>([]);

  async function resolveEntity(refString: string): Promise<ResolvedEntity> {
    const parsed = parseRef(refString);
    if (!parsed) {
      return { ref: refString, type: 'company', id: 0, name: refString, status: 'error' };
    }

    const { type, id } = parsed;
    const entity: ResolvedEntity = { ref: refString, type, id, name: refString, status: 'loading' };

    try {
      if (type === 'customer.account') {
        const res = await fetchCustomerAccount(id);
        const data = res.data;
        entity.name = formatAccountName(data) || refString;
      } else if (type === 'company') {
        const res = await fetchCompany(id);
        entity.name = res.data.name || refString;
      } else if (type === 'product') {
        if (!moduleProductCache) {
          const res = await fetchProducts();
          moduleProductCache = res.data;
        }
        const product = moduleProductCache.find((p) => p.id === id);
        entity.name = product?.name || refString;
      }
      entity.status = 'resolved';
    } catch {
      entity.status = 'error';
    }

    return entity;
  }

  async function resolveAll(refStrings: string[]) {
    const result: ResolvedEntity[] = refStrings.map((r) => {
      if (cache.has(r)) return cache.get(r)!;
      const parsed = parseRef(r);
      return { ref: r, type: parsed?.type ?? 'company', id: parsed?.id ?? 0, name: r, status: 'loading' as const };
    });

    resolvedEntities.value = [...result];

    const toResolve = refStrings.filter((r) => !cache.has(r));
    if (toResolve.length === 0) return;

    const promises = toResolve.map(async (r) => {
      const resolved = await resolveEntity(r);
      cache.set(r, resolved);
    });

    await Promise.allSettled(promises);
    resolvedEntities.value = refStrings.map(
      (ref) => cache.get(ref) ?? { ref, type: 'company', id: 0, name: ref, status: 'loading' as const }
    );
  }

  watch(
    refs,
    (newRefs) => {
      for (const key of cache.keys()) {
        if (!newRefs.includes(key)) cache.delete(key);
      }
      resolveAll(newRefs);
    },
    { immediate: true }
  );

  const isLoading = computed(() => resolvedEntities.value.some((e) => e.status === 'loading'));

  return { resolvedEntities, isLoading };
}
